import React, { useState, useEffect } from "react";
import { Label, TextInput, Card, Spinner, Badge } from "flowbite-react";
import { Users, UserCheck, RefreshCw, CheckCircle, Save } from "lucide-react";
import { toast } from "react-hot-toast";
import { motion } from "framer-motion";
import HeaderWithIcon from "../../components/common/HeaderWithIcon";
import LeftToRightPageWrapper from "../../components/common/LeftToRightPageWrapper ";
import { settingsApi } from "../../api/settings";
import { userApi } from "../../api/user";
import { useAuth } from "../../context/AuthContext";
import { Select } from "flowbite-react";

export default function ReferralSettings() {
  const { user, tokens } = useAuth();
  const [loading, setLoading] = useState(true);
  const [referralSettings, setReferralSettings] = useState({});
  const [users, setUsers] = useState([]);
  const [selectedRole, setSelectedRole] = useState(2);
  const [selectedUser, setSelectedUser] = useState(null);
  const [currentUserName, setCurrentUserName] = useState("");
  const [currentRoleType, setCurrentRoleType] = useState("");
  const [saving, setSaving] = useState(false);
  const [settingsInitialized, setSettingsInitialized] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedUserName, setSelectedUserName] = useState("");

  // Helper function to map role IDs to role names
  const getRoleNameById = (roleId) => {
    const roleMap = {
      2: "Incorporator",
      3: "Sub Operator", 
      4: "General Coordinator",
      5: "Coordinator"
    };
    return roleMap[roleId] || "Unknown Role";
  };

  // Filter users based on search term
  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    fetchReferralSettings();
  }, []);

  const fetchReferralSettings = async () => {
    try {
      setLoading(true);
      const response = await settingsApi.getSettings(
        tokens.access.token,
        user.id,
        "defaultReferralUserId"
      );
      setReferralSettings(response.data);

      if (response.data && response.data.value) {
        // Get user details for the selected referral user
        const getUser = await userApi.getUserById(
          tokens.access.token,
          response.data.value
        );
        
        // Set current user details
        setCurrentUserName(getUser.data.user.name);
        setCurrentRoleType(getRoleNameById(getUser.data.user.role_id));
        
        // Set form state
        setSelectedRole(getUser.data.user.role_id);
        setSelectedUser(getUser.data.user.id);
        setSelectedUserName(getUser.data.user.name);
        setSearchTerm(getUser.data.user.name);
        
        // Fetch users with the correct role pre-selected
        fetchUsers(getUser.data.user.role_id);
        setSettingsInitialized(true);
      } else {
        // No settings yet, just load with default role
        fetchUsers(selectedRole);
      }
    } catch (error) {
      console.error("Error fetching referral settings:", error);
      toast.error("Failed to load referral settings");
      fetchUsers(selectedRole);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async (roleId) => {
    try {
      setLoading(true);
      const response = await userApi.getUsers(
        tokens.access.token,
        1,
        1000,
        roleId
      );
      setUsers(response.data.rows);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = (e) => {
    const newRoleId = e.target.value;
    setSelectedRole(newRoleId);
    setSelectedUser("");
    setSelectedUserName("");
    setSearchTerm("");
    setShowDropdown(false);
    fetchUsers(newRoleId);
  };

  const handleUserSelect = (user) => {
    setSelectedUser(user.id);
    setSelectedUserName(user.name);
    setSearchTerm(user.name);
    setShowDropdown(false);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setShowDropdown(true);
    
    // Clear selection if search term doesn't match selected user name
    if (value !== selectedUserName) {
      setSelectedUser("");
      setSelectedUserName("");
    }
  };

  const handleRefresh = () => {
    fetchUsers(selectedRole);
    toast.success("Refreshed user list");
  };

  const handleSetReferralPreferences = async () => {
    if (!selectedUser) {
      toast.error("Please select a user first");
      return;
    }
    
    try {
      setSaving(true);
      const response = await settingsApi.settings(
        tokens.access.token,
        user.id,
        "defaultReferralUserId",
        selectedUser.toString()
      );
      
      if (response.status === 201) {
        // Update the displayed current settings after successful save
        const selectedUserObj = users.find(u => u.id === parseInt(selectedUser));
        if (selectedUserObj) {
          setCurrentUserName(selectedUserObj.name);
          setCurrentRoleType(getRoleNameById(parseInt(selectedRole)));
          setSettingsInitialized(true);
        }
        
        toast.success("Referral settings saved successfully");
      } else {
        toast.error("Failed to save referral settings");
      }
    } catch (error) {
      console.error("Error saving referral settings:", error);
      toast.error("Failed to save referral settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-1 max-w-3xl mx-auto">
      <div className="space-y-6">
        {settingsInitialized && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Card className="shadow-md border-green-100 bg-green-50">
              <div className="flex items-center mb-2">
                <CheckCircle className="h-6 w-6 text-green-600 mr-2" />
                <h3 className="text-lg font-medium text-gray-800">
                  Current Default Referral User
                </h3>
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-700 font-medium">{currentUserName}</p>
                  <Badge color="info" className="mt-2">{currentRoleType}</Badge>
                </div>
                <button 
                  onClick={fetchReferralSettings} 
                  className="text-blue-600 hover:text-blue-800 p-1 rounded-md"
                >
                  <RefreshCw className="h-4 w-4" />
                </button>
              </div>
            </Card>
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
          <Card className="shadow-md">
            <div className="flex items-center mb-4">
              <UserCheck className="h-6 w-6 text-blue-600 mr-2" />
              <h3 className="text-lg font-medium text-gray-800">
                Set Default Referral User
              </h3>
            </div>
            <p className="text-gray-600 mb-4">
              Select the default user for referral. This user will be used as the
              default referrer if no referral code is provided during registration.
            </p>

            <div className="grid gap-5">
              <div className="space-y-2">
                <Label className="font-medium text-gray-700">User Role</Label>
                <Select
                  value={selectedRole}
                  onChange={handleRoleChange}
                  className="w-full rounded-lg border-gray-300"
                >
                  <option value="2">Incorporator</option>
                  <option value="3">Sub Operator</option>
                  <option value="4">General Coordinator</option>
                  <option value="5">Coordinator</option>
                </Select>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="font-medium text-gray-700">
                    Select User
                  </Label>
                  <button
                    onClick={handleRefresh}
                    className="flex items-center text-xs text-blue-600 hover:text-blue-800"
                    disabled={loading}
                  >
                    <RefreshCw className={`h-3 w-3 mr-1 ${loading ? 'animate-spin' : ''}`} />
                    Refresh
                  </button>
                </div>

                {loading ? (
                  <div className="flex justify-center p-4">
                    <Spinner size="md" />
                  </div>
                ) : (
                  <div className="relative">
                    <TextInput
                      type="text"
                      placeholder="Search for a user..."
                      value={searchTerm}
                      onChange={handleSearchChange}
                      onFocus={() => setShowDropdown(true)}
                      className="w-full"
                    />
                    
                    {showDropdown && filteredUsers.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {filteredUsers.map((user) => (
                          <button
                            key={user.id}
                            className="w-full text-left px-4 py-2 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none border-b border-gray-100 last:border-b-0"
                            onClick={() => handleUserSelect(user)}
                          >
                            <div className="font-medium text-gray-900">{user.name}</div>
                            {user.email && (
                              <div className="text-sm text-gray-500">{user.email}</div>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                    
                    {showDropdown && searchTerm && filteredUsers.length === 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-4 text-gray-500 text-center">
                        No users found matching "{searchTerm}"
                      </div>
                    )}
                  </div>
                )}
                
                {/* Click outside to close dropdown */}
                {showDropdown && (
                  <div 
                    className="fixed inset-0 z-5" 
                    onClick={() => setShowDropdown(false)}
                  />
                )}
              </div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="mt-4"
              >
                <button
                  className={`w-full flex items-center justify-center gap-2 ${
                    !selectedUser 
                      ? 'bg-gray-400 cursor-not-allowed' 
                      : 'bg-blue-600 hover:bg-blue-700'
                  } text-white font-medium py-2 px-4 rounded-lg transition-colors`}
                  onClick={handleSetReferralPreferences}
                  disabled={!selectedUser || saving}
                >
                  {saving ? (
                    <>
                      <Spinner size="sm" light={true} />
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      <span>Set as Default Referrer</span>
                    </>
                  )}
                </button>
              </motion.div>
            </div>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
        >
          <Card className="shadow-md">
            <div className="flex items-center mb-4">
              <Users className="h-6 w-6 text-blue-600 mr-2" />
              <h3 className="text-lg font-medium text-gray-800">
                About Referral Settings
              </h3>
            </div>

            <div className="space-y-3">
              <p className="text-gray-700">
                The default referral user will automatically be assigned to new users 
                who register without providing a referral code. This helps ensure all 
                new users are properly tracked in your referral system.
              </p>
              
              <div className="bg-blue-50 p-3 rounded-md text-blue-800 text-sm mt-2">
                <p className="font-semibold mb-1">Note:</p>
                <p>Changes to the default referral user will only affect new registrations.</p>
              </div>
            </div>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
